import { useEffect, useMemo, useState } from 'react';
import { MoreHorizontalIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurrentUser } from '@/features/identity/hooks/useCurrentUser';
import { VISIBILITY_NAME } from '@/features/workspaces/accessCopy';
import type { MemberView, WorkspaceRole } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';
import { ListCard } from '@/shared/components/ListCard';
import {
  LIST_ROW,
  LIST_ROW_NAME,
  LIST_ROW_SECONDARY,
  LIST_ROWS,
} from '@/shared/components/listRow';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { ARRIVAL_SLIDE_CLASS, useNewIds } from '@/shared/motion/arrival';

import {
  useChangeMemberRole,
  useRemoveMember,
} from '../hooks/useWorkspaceMembers';
import { removalSentence } from '../removalSentence';

export interface MembersSectionProps {
  workspaceId: string;
  members: readonly MemberView[];
  /** Owners and org admins change the list; members read it. */
  canManage: boolean;
  /** Its name, for the sentence that asks an owner before they step down. */
  workspaceName?: string;
}

/** The slices of the list, each with how many it keeps (spec 194 US3). */
const MEMBER_FILTERS = ['all', 'owners', 'members', 'deactivated'] as const;
type MemberFilter = (typeof MEMBER_FILTERS)[number];

const FILTER_WORDS: Record<MemberFilter, string> = {
  all: 'All',
  owners: 'Owners',
  members: 'Members',
  deactivated: 'Deactivated',
};

/** What a slice with no one in it says. `all` cannot be empty while anyone is. */
const EMPTY_FILTER: Record<MemberFilter, string> = {
  all: 'No one in this workspace.',
  owners: 'No owners in this workspace.',
  members: 'Everyone here is an owner.',
  deactivated: 'No one here is deactivated.',
};

function keeps(filter: MemberFilter, member: MemberView): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'owners':
      return member.role === 'owner';
    case 'members':
      return member.role === 'member';
    case 'deactivated':
      return !member.is_active;
  }
}

/** Name or email, as typed: the two things a person is known by here. */
function matches(member: MemberView, needle: string): boolean {
  if (!needle) return true;
  return (
    member.user.display_name.toLowerCase().includes(needle) ||
    member.user.email.toLowerCase().includes(needle)
  );
}

/** A short date in the product's one format: "3 Oct", with the year when not this one. */
function shortDate(at: string, now: Date): string {
  const date = new Date(at);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * How someone came to be here (spec 194 US3). No one added them when the rollout
 * set the members from who had worked in the workspace (service spec 158), so
 * an empty `added_by` reads as that, not as a gap.
 */
function addedLine(member: MemberView, now: Date): string {
  const when = shortDate(member.added_at, now);
  if (member.added_by == null) return 'Joined when access was turned on';
  if (member.added_by.id === member.user.id) return `Joined ${when}`;
  return `Added by ${member.added_by.display_name}, ${when}`;
}

/** What a role change did, for the toast (spec 193 US2). */
function roleSentence(
  name: string,
  role: WorkspaceRole,
  self: boolean,
): string {
  const what = role === 'owner' ? 'an owner' : 'a member';
  return self ? `You are now ${what}.` : `${name} is now ${what}.`;
}

/**
 * How long a person who has just arrived stays tinted. Long enough to be found
 * on a list that has just moved under the eye; the entrance itself is the motion
 * scale's, through `ARRIVAL_SLIDE_CLASS` (spec 196 US6).
 */
const HIGHLIGHT_MS = 2400;

const NOBODY: ReadonlySet<string> = new Set();

/**
 * Who is in the workspace, and the way to change it (spec 186).
 *
 * Owners first, then by name. Owners and org admins add people (verified as
 * organization members at that moment), change roles and remove; anyone may
 * leave. A person whose account is disabled stays listed as Deactivated, so
 * their access comes back with the account.
 *
 * Built to find one person among forty (spec 194 US3): a search by name or
 * email, and slices with counts as the console's lists have them. The role is a
 * select on the row for those who manage, so the ⋯ menu holds only Remove (or
 * Leave on one's own row), and both ask first in the shared dialog, which stays
 * open while the change is saved and shows a refusal inside it.
 *
 * A role change is never a silent click (spec 193 US2): the only owner is not
 * offered the select (the service refuses the demotion), an owner stepping down
 * asks first, and every change says what it did.
 */
export function MembersSection({
  workspaceId,
  members,
  canManage,
  workspaceName,
}: MembersSectionProps) {
  const me = useCurrentUser().data?.id;
  const changeRole = useChangeMemberRole();
  const remove = useRemoveMember();
  const [removing, setRemoving] = useState<MemberView | null>(null);
  const [removalRefusal, setRemovalRefusal] = useState<string | null>(null);
  const [steppingDown, setSteppingDown] = useState(false);
  const [stepDownRefusal, setStepDownRefusal] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<MemberFilter>('all');
  const leaving = removing !== null && removing.user.id === me;
  // The service keeps at least one owner, deactivated or not.
  const owners = members.filter((member) => member.role === 'owner').length;

  // Someone who was not on the last list has just been added — here, from a
  // request, or by another owner — and goes to the top, tinted for a moment.
  const arrived = useNewIds(
    useMemo(() => members.map((member) => ({ id: member.user.id })), [members]),
  );
  // The set `useNewIds` hands back keeps its identity until the list changes, so
  // "this arrival's tint has faded" is that set, recorded when the timer ends.
  const [faded, setFaded] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (arrived.size === 0) return;
    const timer = window.setTimeout(() => {
      setFaded(arrived);
    }, HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [arrived]);
  const tinted = faded === arrived ? NOBODY : arrived;

  const needle = query.trim().toLowerCase();
  const counts = useMemo(() => {
    const result = {} as Record<MemberFilter, number>;
    for (const option of MEMBER_FILTERS) {
      result[option] = members.filter((member) => keeps(option, member)).length;
    }
    return result;
  }, [members]);
  const shown = useMemo(
    () =>
      members
        .filter((member) => keeps(filter, member) && matches(member, needle))
        .sort((a, b) => {
          // At the top only while tinted; then back into its place.
          const fresh =
            Number(tinted.has(b.user.id)) - Number(tinted.has(a.user.id));
          if (fresh !== 0) return fresh;
          if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
          return a.user.display_name.localeCompare(b.user.display_name);
        }),
    [members, filter, needle, tinted],
  );
  const now = new Date();

  const setRole = (
    member: MemberView,
    role: WorkspaceRole,
    onError: (message: string) => void,
    onDone?: () => void,
  ) => {
    changeRole.mutate(
      { workspaceId, userId: member.user.id, role },
      {
        onSuccess: () => {
          onDone?.();
          toast.success(
            roleSentence(member.user.display_name, role, member.user.id === me),
          );
        },
        onError: (failure) => {
          onError(failure.message);
        },
      },
    );
  };

  const chooseRole = (member: MemberView, role: WorkspaceRole) => {
    if (role === member.role) return;
    if (member.role === 'owner' && member.user.id === me) {
      setStepDownRefusal(null);
      setSteppingDown(true);
      return;
    }
    setRole(member, role, (message) => {
      toast.error(message);
    });
  };

  const mine = members.find((member) => member.user.id === me);
  const confirmStepDown = () => {
    if (!mine) return;
    setRole(mine, 'member', setStepDownRefusal, () => {
      setSteppingDown(false);
    });
  };

  const confirmRemoval = () => {
    if (removing === null) return;
    const person = removing;
    setRemovalRefusal(null);
    remove.mutate(
      { workspaceId, userId: person.user.id },
      {
        onSuccess: (removal) => {
          setRemoving(null);
          toast.success(
            removalSentence(
              person.user.display_name,
              removal,
              person.user.id === me,
            ),
          );
        },
        onError: (failure) => {
          setRemovalRefusal(failure.message);
        },
      },
    );
  };

  return (
    <>
      <ListCard<MemberFilter>
        title="Members"
        count={members.length}
        headingId="members-heading"
        description="Members do the work; owners also manage its people, settings, connections and secrets."
        noun={['member', 'members']}
        // Only before the rollout backfill: a workspace made before access
        // control has no members recorded until then (service spec 152).
        state={members.length === 0 ? 'empty' : 'idle'}
        empty={{
          description:
            'No members recorded yet. Workspaces created before access control get their owner and members when the rollout runs.',
        }}
        search={{
          value: query,
          onChange: setQuery,
          placeholder: 'Search by name or email',
          label: 'Search members',
        }}
        chips={{
          label: 'Show',
          options: MEMBER_FILTERS.map((option) => ({
            value: option,
            label: FILTER_WORDS[option],
            count: counts[option],
          })),
          value: filter,
          onChange: setFilter,
        }}
        shown={shown.length}
        total={members.length}
        noResults={{
          description: needle
            ? `No one here matches “${query.trim()}”.`
            : EMPTY_FILTER[filter],
          onClear: needle
            ? () => {
                setQuery('');
              }
            : undefined,
        }}
      >
        <ul aria-label="Members" className={LIST_ROWS}>
          {shown.map((member) => {
            const self = member.user.id === me;
            const onlyOwner = member.role === 'owner' && owners <= 1;
            const changing =
              changeRole.isPending &&
              changeRole.variables.userId === member.user.id;
            return (
              <li
                key={member.user.id}
                aria-label={member.user.display_name}
                className={cn(
                  LIST_ROW,
                  'transition-colors duration-[var(--motion-duration-slow)]',
                  arrived.has(member.user.id) && ARRIVAL_SLIDE_CLASS,
                  tinted.has(member.user.id) && 'bg-primary/5',
                )}
              >
                <UserAvatar
                  id={member.user.id}
                  displayName={member.user.display_name}
                  email={member.user.email}
                  className="size-8"
                />
                {/* On a phone the name has the line beside the avatar and the
                    controls take the next one (spec 265): side by side, a
                    360px screen left the name 70px. */}
                <span className="flex min-w-0 flex-1 flex-col max-md:basis-[calc(100%-2.75rem)]">
                  <span className={LIST_ROW_NAME}>
                    {member.user.display_name}
                    {self ? (
                      <span className="text-muted-foreground font-normal">
                        {' '}
                        (you)
                      </span>
                    ) : null}
                  </span>
                  <span className={LIST_ROW_SECONDARY}>
                    {member.user.email}
                  </span>
                  <span className={LIST_ROW_SECONDARY}>
                    {addedLine(member, now)}
                  </span>
                </span>
                {/* `contents` on a desktop, so the row lays out exactly as
                    before; a line of its own, under the name, on a phone. */}
                <span className="contents max-md:flex max-md:basis-full max-md:items-center max-md:gap-3 max-md:ps-11">
                  {!member.is_active ? (
                    <Badge variant="outline">Deactivated</Badge>
                  ) : null}
                  {canManage && !onlyOwner ? (
                    <Select
                      value={member.role}
                      disabled={changing}
                      onValueChange={(next: string | null) => {
                        if (next === 'owner' || next === 'member') {
                          chooseRole(member, next);
                        }
                      }}
                    >
                      <SelectTrigger
                        aria-label={`Role of ${member.user.display_name}`}
                        className="touch-target w-28"
                      >
                        <SelectValue>
                          {(role: string) =>
                            role === 'owner' ? 'Owner' : 'Member'
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge
                      variant={
                        member.role === 'owner' ? 'secondary' : 'outline'
                      }
                      title={
                        canManage && onlyOwner
                          ? 'A workspace needs an owner. Make someone else an owner first.'
                          : undefined
                      }
                    >
                      {member.role === 'owner' ? 'Owner' : 'Member'}
                    </Badge>
                  )}
                  {canManage || self ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="shrink-0 max-md:ms-auto"
                          >
                            <MoreHorizontalIcon aria-hidden="true" />
                            <span className="sr-only">
                              Actions for {member.user.display_name}
                            </span>
                          </Button>
                        }
                      />
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => {
                            setRemovalRefusal(null);
                            setRemoving(member);
                          }}
                        >
                          {self ? 'Leave workspace' : 'Remove from workspace'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      </ListCard>

      <ConfirmDestructiveDialog
        open={removing !== null}
        title={
          leaving
            ? 'Leave this workspace?'
            : `Remove ${removing?.user.display_name ?? ''}?`
        }
        confirmLabel={leaving ? 'Leave' : 'Remove'}
        pendingLabel={leaving ? 'Leaving…' : 'Removing…'}
        isPending={remove.isPending}
        error={removalRefusal}
        onConfirm={confirmRemoval}
        onCancel={() => {
          setRemoving(null);
          setRemovalRefusal(null);
        }}
      >
        {/* The visibility by its one name (spec 196 US4). */}
        {leaving
          ? `If the workspace is ${VISIBILITY_NAME.organization}, you can still view it; otherwise you lose access. `
          : `If the workspace is ${VISIBILITY_NAME.organization}, they can still view it; otherwise they lose access. `}
        Runs already going finish. Gates assigned to {leaving ? 'you' : 'them'}{' '}
        here go back to the workflow&rsquo;s assignee, and{' '}
        {leaving ? 'your' : 'their'} follows here stop.
      </ConfirmDestructiveDialog>

      <ConfirmDestructiveDialog
        open={steppingDown}
        title="Step down as owner?"
        confirmLabel="Make me a member"
        pendingLabel="Changing…"
        confirmVariant="default"
        isPending={changeRole.isPending}
        error={stepDownRefusal}
        onConfirm={confirmStepDown}
        onCancel={() => {
          setSteppingDown(false);
          setStepDownRefusal(null);
        }}
      >
        You will no longer manage {workspaceName ?? 'this workspace'}: its
        people, settings, connections and secrets. Another owner can make you an
        owner again.
      </ConfirmDestructiveDialog>
    </>
  );
}
